export type ReleaseRecorderLease = () => void;

/**
 * Hold one recorder per signed-in owner across tabs. Chrome has supported the
 * Web Locks API well below our minimum version, so this is the authoritative
 * cross-tab coordinator. Non-browser test environments get a no-op lease.
 */
export async function acquireRecorderLease(ownerId: string): Promise<ReleaseRecorderLease | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return () => {};

  return new Promise((resolve) => {
    let settled = false;
    void locks.request(
      `vd-recorder:${ownerId}`,
      { mode: 'exclusive', ifAvailable: true },
      (lock) => {
        if (!lock) {
          settled = true;
          resolve(null);
          return undefined;
        }
        let releaseHold: (() => void) | null = null;
        const hold = new Promise<void>((release) => { releaseHold = release; });
        let released = false;
        settled = true;
        resolve(() => {
          if (released) return;
          released = true;
          releaseHold?.();
        });
        return hold;
      },
    ).catch(() => {
      if (!settled) resolve(null);
    });
  });
}

/**
 * Every tab that is writing this owner's recordings holds this, shared.
 *
 * The recorder lease answers "is anybody recording", which is not the
 * question sign-out needs: a tab finishing an interrupted upload holds no
 * recorder lease and is still writing the walkthrough that sign-out is about
 * to delete. Asking each tab and waiting for an answer cannot settle it
 * either, because a tab that has not answered yet is indistinguishable from
 * one that is not there.
 *
 * So writers hold this shared, and sign-out takes it exclusively. The browser
 * answers the question instead of a timer: exclusive is granted exactly when
 * the last writer has let go, and a tab that never existed never held it.
 */
export async function holdRecorderWriterLease(ownerId: string): Promise<ReleaseRecorderLease> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return () => {};
  return new Promise((resolve) => {
    let settled = false;
    void locks.request(
      `vd-recorder-writers:${ownerId}`,
      { mode: 'shared' },
      () => {
        let releaseHold: (() => void) | null = null;
        const hold = new Promise<void>((release) => { releaseHold = release; });
        let released = false;
        settled = true;
        resolve(() => {
          if (released) return;
          released = true;
          releaseHold?.();
        });
        return hold;
      },
    ).catch(() => {
      // No lock is worse than a lock, but refusing to write at all is worse
      // than both: a recording nobody can upload is lost either way.
      if (!settled) resolve(() => {});
    });
  });
}

/**
 * Wait until no tab is writing this owner's recordings, then keep it that way
 * until the returned release is called.
 *
 * Null when the wait ran out, which the caller treats as a refusal: what
 * comes after this is deleting the recordings, and doing that alongside a
 * writer loses one.
 */
export async function waitForWriterQuiet(
  ownerId: string,
  timeoutMs: number,
): Promise<ReleaseRecorderLease | null> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return () => {};
  const deadline = new Promise<null>((resolve) => { setTimeout(() => resolve(null), timeoutMs); });
  const exclusive = new Promise<ReleaseRecorderLease>((resolve) => {
    void locks.request(`vd-recorder-writers:${ownerId}`, { mode: 'exclusive' }, () => {
      let releaseHold: (() => void) | null = null;
      const hold = new Promise<void>((release) => { releaseHold = release; });
      let released = false;
      resolve(() => {
        if (released) return;
        released = true;
        releaseHold?.();
      });
      return hold;
    }).catch(() => undefined);
  });
  return await Promise.race([exclusive, deadline]);
}

export async function waitForRecorderLease(
  ownerId: string,
  timeoutMs = 5_000,
): Promise<ReleaseRecorderLease | null> {
  const deadline = Date.now() + timeoutMs;
  do {
    const release = await acquireRecorderLease(ownerId);
    if (release) return release;
    await new Promise((resolve) => setTimeout(resolve, 25));
  } while (Date.now() < deadline);
  return null;
}
